import Typography from '../../../../../kingpin/atoms/Typography';
import {
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { PortalsContext } from '../../../../../contextProviders/SplashScreenProviders/UserAndAccountProviders/PortalsProvider';
import { getMetricLink } from '../../../AllSets/routes';
import FieldUsageItem from './FieldUsageItem';
import Card from '../../../../../components/Common/Card';
import { FieldUsageContext } from './FieldUsage';
import { EnrichedFieldUsage } from './types';
import DashboardFieldUsage from './DashboardFieldUsage';
import ReportFieldUsage from './ReportFieldUsage';
import {
  buildShowBoard,
  buildShowGlobalGoal,
  buildShowGlobalKpiList,
} from '../../../../../navigation/appRoutes';
import Accordion from '../../../../../kingpin/atoms/Accordion';
import ErrorBoundary from '../../../../../components/Common/ErrorBoundary';

const Section = ({
  title,
  children,
}: {
  title: string;
  children?: ReactElement | ReactElement[];
}) => (
  <div style={{ marginBottom: 16 }}>
    <Typography.Body type={'Body 13 Bold'}>{title}</Typography.Body>
    {children}
  </div>
);

type ListItem = { key: string; name: string; link: string; category?: string };

const FieldUsage = ({ fieldUsage }: { fieldUsage: EnrichedFieldUsage }) => {
  const { selectedPortal } = useContext(PortalsContext);
  const [expandedSections, setExpandedSections] = useState<{
    [key: string]: boolean;
  }>({
    metrics: false,
    savedFilters: false,
    defaultDatasetFilters: false,
    scorecards: false,
    goals: false,
    dashboards: false,
    reports: false,
    apps: false,
    workspaces: false,
    performanceBoards: false,
  });

  const toggleSection = useCallback(
    (sectionName: string) => {
      const selectedSection = { [sectionName]: !expandedSections[sectionName] };
      setExpandedSections({ ...expandedSections, ...selectedSection });
    },
    [expandedSections],
  );

  const metricItems: ListItem[] = useMemo(
    () =>
      fieldUsage.metrics.map((m) => ({
        key: m.id,
        name: m.name,
        metricId: m.id,
        link: getMetricLink({
          metric: m,
          portal: selectedPortal,
        }),
      })),
    [fieldUsage.metrics, selectedPortal],
  );

  const savedFilterItems: ListItem[] = useMemo(
    () =>
      fieldUsage.savedFilters.map((sf: SavedFilter) => ({
        key: sf.id,
        name: sf.name || sf.dataset,
        link: '',
      })),
    [fieldUsage.savedFilters],
  );

  const defaultDatasetFilterItems: ListItem[] = useMemo(
    () =>
      fieldUsage.defaultDatasetFilters.map((df: DefaultDatasetFilter) => ({
        key: df.id,
        name: df.name || df.dataset,
        link: '',
      })),
    [fieldUsage?.defaultDatasetFilters],
  );

  const scorecardItems: ListItem[] = useMemo(
    () =>
      fieldUsage.scorecards.map((sc) => ({
        key: sc.id,
        name: sc.title,
        link: buildShowGlobalKpiList({
          scorecardId: sc.id,
          portal: selectedPortal,
        }),
      })),
    [fieldUsage.scorecards, selectedPortal],
  );

  const goalItems: ListItem[] = useMemo(
    () =>
      fieldUsage.goals.map((g) => ({
        key: g.id,
        name: g.title,
        link: buildShowGlobalGoal(g.id),
      })),
    [fieldUsage.goals],
  );
  const boardItems: ListItem[] = useMemo(
    () =>
      fieldUsage.performanceBoards.map((b) => ({
        key: b.id,
        name: b.name,
        link: buildShowBoard(b.id),
      })),
    [fieldUsage.performanceBoards],
  );

  return (
    <Card style={{ paddingLeft: 10, paddingRight: 10 }}>
      {metricItems.length > 0 && (
        <Accordion
          isOpen={expandedSections['metrics']}
          openedHeight={400}
          onToggled={() => toggleSection('metrics')}
          text={`Metrics (${metricItems.length})`}
        >
          {metricItems.map((m) => (
            <FieldUsageItem {...m} />
          ))}
        </Accordion>
      )}
      {savedFilterItems.length > 0 && (
        <Accordion
          isOpen={expandedSections['savedFilters']}
          openedHeight={400}
          onToggled={() => toggleSection('savedFilters')}
          text={`Saved Filters (${savedFilterItems.length})`}
        >
          {savedFilterItems.map((sf) => (
            <FieldUsageItem {...sf} />
          ))}
        </Accordion>
      )}
      {defaultDatasetFilterItems.length > 0 && (
        <Accordion
          isOpen={expandedSections['defaultDatasetFilters']}
          openedHeight={400}
          onToggled={() => toggleSection('defaultDatasetFilters')}
          text={`Default Dataset Filters (${defaultDatasetFilterItems.length})`}
        >
          {defaultDatasetFilterItems.map((df) => (
            <FieldUsageItem {...df} />
          ))}
        </Accordion>
      )}
      {fieldUsage.scorecards.length > 0 && (
        <Accordion
          isOpen={expandedSections['scorecards']}
          openedHeight={400}
          onToggled={() => toggleSection('scorecards')}
          text={`Scorecards (${fieldUsage.scorecards.length})`}
        >
          {scorecardItems.map((sc) => (
            <FieldUsageItem {...sc} />
          ))}
        </Accordion>
      )}
      {fieldUsage.performanceBoards.length > 0 && (
        <Accordion
          isOpen={expandedSections['boards']}
          openedHeight={400}
          onToggled={() => toggleSection('boards')}
          text={`Boards (${fieldUsage.performanceBoards.length})`}
        >
          {boardItems.map((b) => (
            <FieldUsageItem {...b} />
          ))}
        </Accordion>
      )}
      {fieldUsage.goals.length > 0 && (
        <Accordion
          isOpen={expandedSections['goals']}
          openedHeight={400}
          onToggled={() => toggleSection('goals')}
          text={`Goals (${fieldUsage.goals.length})`}
        >
          {goalItems.map((g) => (
            <FieldUsageItem {...g} />
          ))}
        </Accordion>
      )}
      {fieldUsage.dashboards.length > 0 && (
        <Accordion
          isOpen={expandedSections['dashboards']}
          openedHeight={400}
          onToggled={() => toggleSection('dashboards')}
          text={`Dashboards (${fieldUsage.dashboards.length})`}
        >
          {fieldUsage.dashboards.map((d) => (
            <DashboardFieldUsage key={d.id} dashboard={d} usage={fieldUsage} />
          ))}
        </Accordion>
      )}
      {fieldUsage.reports.length > 0 && (
        <Accordion
          isOpen={expandedSections['reports']}
          openedHeight={400}
          onToggled={() => toggleSection('reports')}
          text={`Reports (${fieldUsage.reports.length})`}
        >
          {fieldUsage.reports.map((r) => (
            <ReportFieldUsage key={r.id} report={r} usage={fieldUsage} />
          ))}
        </Accordion>
      )}
      {fieldUsage.apps.length > 0 && (
        <Accordion
          isOpen={expandedSections['apps']}
          openedHeight={400}
          onToggled={() => toggleSection('apps')}
          text={`Apps (${fieldUsage.apps.length})`}
        >
          {fieldUsage.apps.map((a) => (
            <FieldUsageItem key={a.id} link={a.link} name={a.label} />
          ))}
        </Accordion>
      )}
      {fieldUsage.workspaces.length > 0 && (
        <Accordion
          isOpen={expandedSections['workspaces']}
          openedHeight={400}
          onToggled={() => toggleSection('workspaces')}
          text={`Workspaces (${fieldUsage.workspaces.length})`}
        >
          <Section title={`Workspaces (${fieldUsage.workspaces.length})`}>
            {fieldUsage.workspaces.map((a) => (
              <FieldUsageItem key={a.id} link={a.link} name={a.label} />
            ))}
          </Section>
        </Accordion>
      )}
    </Card>
  );
};

const Gate = ({ field }: { field: string }) => {
  const { usages } = useContext(FieldUsageContext);

  const fieldUsage = usages[field];

  if (!fieldUsage) {
    return null;
  }
  return (
    <ErrorBoundary>
      <FieldUsage fieldUsage={fieldUsage} />
    </ErrorBoundary>
  );
};
export default Gate;
